import React, {useEffect, useState} from 'react';
import {Checkbox, FormControlLabel, TextField, Button, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper} from '@mui/material';
import useAxiosInstance from "../useAxiosInstance";
import Swal from "sweetalert2";
import NavBar, {role} from "./NavBar";

const day = (offset = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return d.toISOString().slice(0, 10);
};

// Columns in the order the Device Testing screen runs them
const TESTS = [
    ["wifi", "Wi-Fi"], ["bluetooth", "BT"], ["buttons", "Buttons"], ["ppg", "PPG"],
    ["mainElectrodes", "Main el."], ["topElectrodes", "Top el."], ["externalElectrodes", "Ext. el."],
    ["laserDiodes", "Laser"], ["externalJack", "Jack"], ["sound", "Sound"], ["charging", "Charging"],
    ["epRom", "EEPROM"], ["annaB112", "ANNA-B"], ["annaBBleConnectivity", "ANNA-B BLE"], ["powerButton", "Power"], ["lcd", "LCD"],
    ["bifilarCoil", "Coil"], ["autoRotate", "Rotate"],
];

const colour = (v) => {
    const s = (v || "").toLowerCase();
    return s === "pass" ? "#1b8a3a" : s === "fail" ? "#c62828" : "#777";
};

// Date inputs: four-digit years only (the browser otherwise accepts e.g. 12025)
const DATE_LIMITS = {min: "2020-01-01", max: "2099-12-31"};

export default function Fct() {
    const instance = useAxiosInstance();
    const [filter, setFilter] = useState({deviceId: "", testType: "", from: day(-7), to: day(), latestOnly: true});
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const canDownload = role() !== "factory";
    const isAdmin = role() === "admin";

    const query = () => {
        const p = new URLSearchParams({
            from: `${filter.from}T00:00:00`, to: `${filter.to}T23:59:59`, latestOnly: filter.latestOnly,
        });
        if (filter.deviceId) p.append("deviceId", filter.deviceId.trim());
        if (filter.testType) p.append("testType", filter.testType);
        return p.toString();
    };

    const load = () => {
        setLoading(true);
        instance.get(`Fct/Results?${query()}`).then(setRows).catch(() => {}).finally(() => setLoading(false));
    };

    // Files come through the API (authenticated), not public links
    const download = (url, name) => {
        instance.get(url, {responseType: "blob"}).then((blob) => {
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = name;
            a.click();
            URL.revokeObjectURL(a.href);
        }).catch(() => {});
    };

    // Stage, dates and "latest run" apply at once; the device ID on Search or Enter
    useEffect(load, [filter.testType, filter.from, filter.to, filter.latestOnly]); // eslint-disable-line react-hooks/exhaustive-deps

    const remove = (r) => {
        Swal.fire({
            text: `Delete the ${r.testType} result of ${r.deviceId} from ${r.createdAt?.replace("T", " ").slice(0, 16)} UTC (${r.station})? This can't be undone.`,
            showCancelButton: true, confirmButtonText: "Delete", returnFocus: false,
        }).then((res) => {
            if (!res.isConfirmed) return;
            setLoading(true);
            instance.delete(`Fct/Results/${r.id}`).then(load).catch(() => setLoading(false));
        });
    };

    return (
        <div style={{margin: "auto", maxWidth: "1800px"}}>
            <NavBar title="FCT results." loading={loading}/>
            <div className="d-flex align-items-center gap-2 flex-wrap mx-3 mb-3">
                <TextField label="Device ID" size="small" value={filter.deviceId}
                           onChange={e => setFilter({...filter, deviceId: e.target.value})}
                           onKeyDown={e => { if (e.key === "Enter") load(); }}/>
                <TextField label="Stage" select size="small" style={{minWidth: 140}} SelectProps={{native: true}}
                           InputLabelProps={{shrink: true}} value={filter.testType}
                           onChange={e => setFilter({...filter, testType: e.target.value})}>
                    <option value="">All</option>
                    <option value="Board">Board</option>
                    <option value="Assembled">Assembled</option>
                </TextField>
                <TextField label="From" type="date" size="small" InputLabelProps={{shrink: true}} inputProps={DATE_LIMITS} value={filter.from}
                           onChange={e => setFilter({...filter, from: e.target.value})}/>
                <TextField label="To" type="date" size="small" InputLabelProps={{shrink: true}} inputProps={DATE_LIMITS} value={filter.to}
                           onChange={e => setFilter({...filter, to: e.target.value})}/>
                <FormControlLabel label="Latest run per unit and stage" control={
                    <Checkbox checked={filter.latestOnly} onChange={() => setFilter({...filter, latestOnly: !filter.latestOnly})}/>}/>
                <Button variant="contained" onClick={load}>Search</Button>
                {canDownload &&
                    <Button variant="outlined" onClick={() => download(`Fct/Results.csv?${query()}`, `fct-${filter.from}-${filter.to}.csv`)}>
                        CSV
                    </Button>}
                <span className="ms-2">{rows.length} result(s)</span>
            </div>
            <TableContainer component={Paper} className="mx-3" style={{width: "auto"}}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Time (UTC)</TableCell>
                            <TableCell>Device</TableCell>
                            <TableCell>Stage</TableCell>
                            <TableCell>Station</TableCell>
                            <TableCell>PCB</TableCell>
                            <TableCell>OS / App / Hub</TableCell>
                            {TESTS.map(([k, label]) => <TableCell key={k}>{label}</TableCell>)}
                            <TableCell>PPG file</TableCell>
                            {isAdmin && <TableCell/>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(r => (
                            <TableRow key={r.id}>
                                <TableCell>{r.createdAt?.replace("T", " ").slice(0, 19)}</TableCell>
                                <TableCell>{r.deviceId}</TableCell>
                                <TableCell>{r.testType}</TableCell>
                                <TableCell>{r.station}</TableCell>
                                <TableCell>{r.pcbRevision}</TableCell>
                                <TableCell>{[r.osVersion, r.appVersion, r.hubVersion].map(v => v || "-").join(" / ")}</TableCell>
                                {TESTS.map(([k]) => <TableCell key={k} style={{color: colour(r[k])}}>{r[k] || "-"}</TableCell>)}
                                <TableCell>
                                    {r.ppgBlob && canDownload &&
                                        <Button size="small" onClick={() => download(`Fct/Results/${r.id}/Ppg`, `${r.deviceId}-ppg`)}>
                                            Download
                                        </Button>}
                                </TableCell>
                                {isAdmin && <TableCell>
                                    <Button size="small" color="error" onClick={() => remove(r)}>Delete</Button>
                                </TableCell>}
                            </TableRow>))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}
